/**
 * Module-level cache for "reference data" lists (users, sectors, enclosures,
 * animals) shared across many views and card components.
 *
 * Why: TaskCard, TaskManagementView, SectorDashboard, etc. each call the same
 * list connectors at mount. With N cards on screen this multiplies into dozens
 * of redundant Firestore reads. This cache:
 *   - dedups concurrent fetches via in-flight Promise sharing
 *   - serves results for `ttl` ms after a successful fetch
 *   - is invalidated explicitly when the underlying data changes
 *
 * Not a persistent cache — cleared on full reload.
 */

const DEFAULT_TTL_MS = 60_000;

const cache = new Map(); // key -> { data, timestamp }
const inFlight = new Map(); // key -> Promise

export function cachedFetch(key, fetcher, { ttl = DEFAULT_TTL_MS } = {}) {
  const now = Date.now();
  const cached = cache.get(key);
  if (cached && now - cached.timestamp < ttl) {
    return Promise.resolve(cached.data);
  }
  const pending = inFlight.get(key);
  if (pending) return pending;

  const promise = Promise.resolve()
    .then(() => fetcher())
    .then((data) => {
      cache.set(key, { data, timestamp: Date.now() });
      inFlight.delete(key);
      return data;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });
  inFlight.set(key, promise);
  return promise;
}

export function invalidate(key) {
  cache.delete(key);
}

export function invalidatePrefix(prefix) {
  for (const k of cache.keys()) {
    if (k.startsWith(prefix)) cache.delete(k);
  }
}

export function clearAll() {
  cache.clear();
  inFlight.clear();
}
